import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateUserMutation } from 'data/__generated__';
import { User } from 'lib/User';
import { useNavigate } from 'react-router-dom';

import { UserContext } from 'constants/user-context';

import { Logo } from 'components/Logo';

import { TotemButton } from 'ui/Button';
import { InlineError } from 'ui/InlineError';

import { validatePhone } from 'utils';

import { LoginStyle, LoginBottomHalfStyle, LoginTopHalfStyle, LoginWrapperStyle } from 'styles/pages/login.styles';
import { SignupForm, SignupTitleStyle, SignupOneRow } from 'styles/pages/signup.styles';
import { TotemLabel, TotemInput } from 'styles';
import { SetPasswordProps } from 'pages/SetPassword/SetPassword.types';

interface FormValues {
    firstname: string;
    lastname: string;
    jobTitle?: string;
    phone?: string;
}
export const SetPasswordInfo: React.FC<SetPasswordProps> = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const isGlobalEmployee = User.isGlobalEmployee();
    const [updateUser] = useUpdateUserMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            firstname: user?.profile?.firstname ?? '',
            lastname: user?.profile?.lastname ?? '',
            jobTitle: user?.profile?.jobTitle ?? '',
            phone: user?.profile?.phone ?? '',
        },
    });

    const handleFieldsSubmit = async (data: FormValues) => {
        const profile = user?.profile;
        const { firstname, lastname } = data;

        const updatedProfile = {
            ...profile,
            ...data,
            fullname: `${firstname} ${lastname}`,
        };
        if (!user?._id) {
            return;
        }
        try {
            await updateUser({
                variables: {
                    data: {
                        profile: {
                            ...updatedProfile,
                        },
                    },
                    _id: user?._id,
                },
                refetchQueries: ['GET_USER'],
            });
            navigate('/', { relative: 'path' });
        } catch (error: any) {
            if (window && window.Log && typeof window.Log.error === 'function') {
                window.Log.error(error);
            }
        }
    };

    return (
        <LoginStyle>
            <LoginTopHalfStyle>
                <Logo dark />
            </LoginTopHalfStyle>
            <LoginBottomHalfStyle>
                <LoginWrapperStyle>
                    <SignupTitleStyle>Faisons connaissance</SignupTitleStyle>

                    <SignupForm onSubmit={handleSubmit(handleFieldsSubmit)}>
                        <SignupOneRow>
                            <TotemLabel color="white" $marginMultiplier={[1, 0, 0, 0]}>
                                Prénom <InlineError display={errors.firstname} />
                                <TotemInput
                                    placeholder="Entrez votre prénom"
                                    type="text"
                                    autoFocus
                                    {...register('firstname', { required: true })}
                                    data-test="set-firstname"
                                />
                            </TotemLabel>
                            <TotemLabel color="white" $marginMultiplier={[1, 0, 0, 0]}>
                                Nom <InlineError display={errors.lastname} />
                                <TotemInput
                                    placeholder="Entrez votre nom"
                                    type="text"
                                    {...register('lastname', { required: true })}
                                    data-test="set-lastname"
                                />
                            </TotemLabel>
                        </SignupOneRow>
                        {!isGlobalEmployee && (
                            <SignupOneRow>
                                <TotemLabel color="white" $marginMultiplier={[0, 0.5, 1, 0]}>
                                    Rôle <InlineError display={errors.jobTitle} />
                                    <TotemInput
                                        placeholder="Entrez votre rôle dans l'entreprise"
                                        type="text"
                                        {...register('jobTitle', { required: true })}
                                        data-test="set-role"
                                    />
                                </TotemLabel>
                                <TotemLabel color="white" $marginMultiplier={[0, 0, 1, 0.5]}>
                                    Numéro de téléphone <InlineError display={errors.phone} />
                                    <TotemInput
                                        placeholder="Entrez votre numéro de téléphone"
                                        type="text"
                                        {...register('phone', {
                                            required: true,
                                            validate: (value) =>
                                                validatePhone("Votre numéro de téléphone n'est pas valide")(
                                                    value ?? '',
                                                ),
                                        })}
                                        data-test="set-phone-number"
                                    />
                                </TotemLabel>
                            </SignupOneRow>
                        )}
                        <TotemButton $margins={[0, 0.5]} type="submit" data-test="set-password-submit">
                            Suivant
                        </TotemButton>
                    </SignupForm>
                </LoginWrapperStyle>
            </LoginBottomHalfStyle>
        </LoginStyle>
    );
};
